import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import toast from 'react-hot-toast';
import { User, Lock, Phone, Mail } from 'lucide-react';

export default function Profile() {
  const { user, loadUser } = useAuth();
  const [showPwd, setShowPwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwdForm.newPassword !== pwdForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwdForm.newPassword.length < 8) { toast.error('Minimum 8 characters'); return; }
    setSaving(true);
    try {
      await api.put('/auth/change-password', { currentPassword: pwdForm.currentPassword, newPassword: pwdForm.newPassword });
      toast.success('Password changed');
      setPwdForm({ currentPassword: '', newPassword: '', confirm: '' });
      setShowPwd(false);
      loadUser();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>

      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-brand-600 rounded-full flex items-center justify-center text-2xl font-bold text-white">{user?.name?.[0]}</div>
          <div>
            <h2 className="text-lg font-semibold">{user?.name}</h2>
            <p className="text-sm text-gray-500 capitalize">{user?.role}</p>
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-3 py-2 border-b">
            <Mail size={16} className="text-gray-400" />
            <span className="text-gray-500">Email:</span>
            <span className="font-medium">{user?.email}</span>
          </div>
          <div className="flex items-center gap-3 py-2 border-b">
            <Phone size={16} className="text-gray-400" />
            <span className="text-gray-500">Phone:</span>
            <span className="font-medium">{user?.phone || 'Not set'}</span>
          </div>
          <div className="flex items-center gap-3 py-2 border-b">
            <User size={16} className="text-gray-400" />
            <span className="text-gray-500">Role:</span>
            <span className="font-medium capitalize">{user?.role}</span>
          </div>
          <div className="flex items-center gap-3 py-2">
            <Lock size={16} className="text-gray-400" />
            <span className="text-gray-500">Last Login:</span>
            <span className="font-medium">{user?.last_login_at ? new Date(user.last_login_at).toLocaleString('en-IN') : 'N/A'}</span>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6">
        <button onClick={() => setShowPwd(!showPwd)} className="flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700">
          <Lock size={16} /> Change Password
        </button>

        {showPwd && (
          <form onSubmit={changePassword} className="mt-4 space-y-3">
            <input type="password" value={pwdForm.currentPassword} onChange={e => setPwdForm(f => ({ ...f, currentPassword: e.target.value }))}
              placeholder="Current Password" required className="w-full px-3 py-2 border rounded-lg text-sm" />
            <input type="password" value={pwdForm.newPassword} onChange={e => setPwdForm(f => ({ ...f, newPassword: e.target.value }))}
              placeholder="New Password (min 8 chars)" required className="w-full px-3 py-2 border rounded-lg text-sm" />
            <input type="password" value={pwdForm.confirm} onChange={e => setPwdForm(f => ({ ...f, confirm: e.target.value }))}
              placeholder="Confirm New Password" required className="w-full px-3 py-2 border rounded-lg text-sm" />
            <button type="submit" disabled={saving} className="w-full py-2 bg-brand-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
              {saving ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
